import { api, fetchServer } from "@/features/utils/fetch-server";
import type {
  AdminEntityRegistry,
  AdminEntitySchema,
  AdminRow,
  AdminSearchOptions,
  AdminSearchResult,
  GlobalSearchResponse,
  TenantDetail,
} from "../types";

const base = "/api/admin/v1";

/**
 * Serialise search options the way `routes-entities.ts` reads them back:
 * scalars as plain params, each column filter as `filter_<column>`, JSON-encoded
 * when it carries an operator so the server can tell `status=failed` from
 * `created_at >= …`.
 */
export const toSearchParams = (
  options: AdminSearchOptions = {},
): URLSearchParams => {
  const params = new URLSearchParams();

  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.offset) params.set("offset", String(options.offset));
  if (options.sort_by) params.set("sort_by", options.sort_by);
  if (options.sort_order) params.set("sort_order", options.sort_order);
  if (options.search) params.set("search", options.search);
  if (options.client_id) params.set("client_id", options.client_id);
  if (options.date_from !== undefined)
    params.set("date_from", String(options.date_from));
  if (options.date_to !== undefined)
    params.set("date_to", String(options.date_to));
  if (options.date_field) params.set("date_field", options.date_field);
  if (options.include_deleted) params.set("include_deleted", "true");

  for (const [column, filter] of Object.entries(options.filters || {})) {
    if (filter === undefined) continue;
    params.set(
      `filter_${column}`,
      filter !== null && typeof filter === "object"
        ? JSON.stringify(filter)
        : String(filter),
    );
  }

  return params;
};

/** Read-only client of the generic back-office API (SYSADMIN-only server-side). */
export class BackofficeAPI {
  /** Every browsable entity, and the groups they hang under. */
  static getRegistry = async (): Promise<AdminEntityRegistry> =>
    api.get<AdminEntityRegistry>(`${base}/entities`);

  /** The columns of one table, and how the grid should present them. */
  static getSchema = async (table: string): Promise<AdminEntitySchema> =>
    api.get<AdminEntitySchema>(`${base}/entities/${table}/schema`);

  /** One page of rows. */
  static search = async (
    table: string,
    options: AdminSearchOptions = {},
  ): Promise<AdminSearchResult> =>
    api.get<AdminSearchResult>(
      `${base}/entities/${table}?${toSearchParams(options)}`,
    );

  /** One row, by its key. */
  static getRow = async (table: string, id: string): Promise<AdminRow> => {
    const res = await api.get<{ data: AdminRow }>(
      `${base}/entities/${table}/${encodeURIComponent(id)}`,
    );
    return res.data;
  };

  /** The audit trail of one row — empty on a non-auditable entity. */
  static getHistory = async (
    table: string,
    id: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<AdminSearchResult> => {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.offset) params.set("offset", String(options.offset));
    return api.get<AdminSearchResult>(
      `${base}/entities/${table}/${encodeURIComponent(id)}/history?${params}`,
    );
  };

  /** One term across every searchable entity. */
  static globalSearch = async (
    query: string,
    options: {
      tables?: string[];
      client_id?: string;
      per_table?: number;
      include_deleted?: boolean;
    } = {},
  ): Promise<GlobalSearchResponse> => {
    const params = new URLSearchParams({ q: query });
    if (options.tables?.length) params.set("tables", options.tables.join(","));
    if (options.client_id) params.set("client_id", options.client_id);
    if (options.per_table) params.set("per_table", String(options.per_table));
    if (options.include_deleted) params.set("include_deleted", "true");
    return api.get<GlobalSearchResponse>(`${base}/entities/search?${params}`);
  };

  /** Everything about one tenant, in a single round-trip. */
  static getTenantDetail = async (clientId: string): Promise<TenantDetail> =>
    api.get<TenantDetail>(`${base}/tenants/${clientId}`);

  /**
   * The current filters as a CSV. Goes through `fetchServer` rather than a
   * plain link because the route needs the bearer token; the blob is handed to
   * the browser as a download.
   */
  static exportCsv = async (
    table: string,
    options: AdminSearchOptions = {},
  ): Promise<void> => {
    const params = toSearchParams({ ...options, offset: 0, limit: 1000 });
    const response = await fetchServer(
      `${base}/entities/${table}/export.csv?${params}`,
      { method: "GET" },
    );
    const blob = await response.blob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${table}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
}
